import type { ReactNode } from "react";
import { useMemo, useState } from "react";
import { can } from "@sts/schemas";
import { scanPlanRooms, scanRoomGroups } from "@sts/geometry";
import type { Me, SurveyScan, SurveySummary, SurveySurface } from "../api/types.js";
import { Async } from "../components/Async.jsx";
import { SurfacePanel, adjacencyLabel } from "../components/SurfacePanel.jsx";
import { RoomMiniPlan } from "../components/RoomMiniPlan.jsx";
import { RoomIdentity } from "../components/RoomIdentity.jsx";
import { usePortal } from "../app/context.js";
import { useAsync } from "../app/use-async.js";
import { href } from "../app/router.js";

/**
 * One room, and nothing else.
 *
 * The rooms list says a room owes six answers; before this, the only way to
 * give them was the whole drawing — the plan, the checks, the comments, the
 * design check, every other room's walls — and then finding that room's walls
 * among them. Six answers, and a screen with forty things on it.
 *
 * Here the room's own walls, floors and openings are a list, each saying what
 * is on the other side of it, and picking one opens the same editor the plan
 * uses. Nothing on the screen belongs to any other room.
 *
 * THE OUTSTANDING ONES COME FIRST, and they are counted in the heading, so
 * "six walls to answer" is answered by scrolling from the top and stopping
 * when the amber runs out.
 */

interface Page {
  readonly survey: SurveySummary;
  readonly me: Me;
  /**
   * Whether this survey counts heat between heated rooms.
   *
   * It lives on the design conditions, not the summary, and it decides which
   * surfaces are waiting on anybody: with it off, an internal wall is taken at
   * U = 0 and confirming it changes no watt. Read here so this screen's count
   * cannot disagree with the row that sent somebody to it.
   */
  readonly countsInternal: boolean;
  /** Null when this survey has no scan to draw from. The list still works. */
  readonly scan: SurveyScan | null;
}

export function SurveyRoom({
  surveyId,
  roomId,
}: {
  readonly surveyId: string;
  readonly roomId: string;
}): ReactNode {
  const { client } = usePortal();
  const { state, reload } = useAsync<Page>(async () => {
    const [survey, me, conditions, scan] = await Promise.all([
      client.getSurvey(surveyId),
      client.me(),
      // Through a promise so a client without the call — an older API — leaves
      // the flag at its default rather than failing the whole screen.
      Promise.resolve()
        .then(() => client.designConditions(surveyId))
        .catch(() => null),
      // The geometry for the little plan. A survey with no scan, or one drawn
      // by hand, simply has no picture: the list still works, so this must not
      // be able to fail the screen.
      Promise.resolve()
        .then(() => client.surveyScan(surveyId))
        .catch(() => null),
    ]);
    return {
      survey,
      me,
      countsInternal: conditions?.countHeatBetweenRooms === true,
      scan,
    };
  }, [client, surveyId]);

  return (
    <Async state={state} what="this room" reload={reload}>
      {(page) => <Body page={page} roomId={roomId} reload={reload} />}
    </Async>
  );
}

/**
 * Whether this surface is still waiting on somebody.
 *
 * The same rule the list and the API count by: a figure nobody has confirmed,
 * which came from an assumption rather than a measurement. A surface the
 * calculation takes at U = 0 is not waiting on anybody — confirming it could
 * not change a watt — and the API's own count leaves those out, so a room
 * screen that counted them would disagree with the row that sent you here.
 */
function outstanding(surface: SurveySurface, countsInternal: boolean): boolean {
  if (surface.uValue?.confirmedBySurveyor === true) return false;
  const provenance = surface.uValue?.provenance;
  if (provenance !== "age-band-default" && provenance !== "epc" && provenance !== "derived") {
    return false;
  }
  if (surface.adjacency === "party") return false;
  if (!countsInternal && surface.adjacency === "adjacent-room") return false;
  return true;
}

/**
 * The room's remaining work, with the two questions named rather than added up.
 *
 * "6 things still need an answer" was a count of U-values alone, so a surveyor
 * who answered what was on the other side of a wall - the FIRST thing the
 * panel asks - watched the number and the row's badge both stay exactly as
 * they were, and reasonably concluded the save had not worked. Naming them
 * costs a longer sentence and is worth it.
 */
function stillToAnswer(uValues: number, otherSides: number): string {
  const parts = [
    uValues === 0 ? null : `${uValues} U-value${uValues === 1 ? "" : "s"}`,
    otherSides === 0 ? null : `${otherSides} other side${otherSides === 1 ? "" : "s"}`,
  ].filter((p): p is string => p !== null);
  if (parts.length === 0) return "Everything in this room is answered.";
  return `Still to answer: ${parts.join(" and ")}.`;
}

function Body({
  page,
  roomId,
  reload,
}: {
  readonly page: Page;
  readonly roomId: string;
  readonly reload: () => void;
}): ReactNode {
  const room = page.survey.rooms.find((r) => r.id === roomId);
  const [picked, setPicked] = useState<string | null>(null);
  /** The surface under the pointer, which the plan picks out without a click. */
  const [hovered, setHovered] = useState<string | null>(null);
  const countsInternal = page.countsInternal;

  /*
   * This room's own shape, found by the identity the conversion wrote.
   *
   * `rooms.external_ref` IS `ScanRoomGroup.externalRef`, which is what
   * `scanPlanRooms` carries through — so the join is exact rather than a match
   * on a name that two rooms could share.
   */
  const planRooms = useMemo(() => {
    const capture = page.scan?.scan?.capture ?? null;
    if (capture === null) return null;
    try {
      return scanPlanRooms(scanRoomGroups(capture));
    } catch {
      // A capture this build cannot read is a missing picture, not a broken
      // screen: the walls are still answerable from the list.
      return null;
    }
  }, [page.scan]);

  /** Only the floor this room is on: a flat above shown below it is a lie. */
  const floorRooms = useMemo(() => {
    if (planRooms === null || room?.externalRef == null) return null;
    const mine = planRooms.find((r) => r.externalRef === room.externalRef);
    if (mine === undefined) return null;
    return planRooms.filter((r) => r.story === mine.story);
  }, [planRooms, room?.externalRef]);

  const canEdit = can({
    role: page.me.role,
    resource: "survey",
    action: "update",
    isAssigned: true,
  });

  // Outstanding first, then by what they are, so the work is at the top and
  // the rest reads as a record of the room.
  const surfaces = useMemo(() => {
    const all = room?.surfaces ?? [];
    return [...all].sort((a, b) => {
      const owed = Number(outstanding(b, countsInternal)) - Number(outstanding(a, countsInternal));
      if (owed !== 0) return owed;
      return (a.name ?? a.kind).localeCompare(b.name ?? b.kind);
    });
  }, [room, countsInternal]);

  if (room === undefined) {
    return (
      <div className="state state--empty">
        <p className="state__title">That room is not on this survey</p>
        <p className="state__body">
          It may have been renamed or moved to another floor by a re-scan.{" "}
          <a href={href.surveyRooms(page.survey.id)}>Back to the rooms</a>.
        </p>
      </div>
    );
  }

  const owed = surfaces.filter((s) => outstanding(s, countsInternal)).length;
  /*
   * The OTHER question, counted apart.
   *
   * A surface carries two of them - what is on the other side of it, and what
   * it is made of - and only the second has ever been counted here, because
   * that is the count the API keeps and the rooms list shows. Saying "6 things
   * still need an answer" while the panel asks two questions about each
   * surface made answering the first one look like it had done nothing.
   */
  const assumedSides = surfaces.filter((s) => s.adjacencyAssumed === true).length;
  const chosen = surfaces.find((s) => s.id === picked) ?? null;

  /*
   * Which wall the plan picks out: the one under the pointer, else the one
   * open. Pointing is the cheaper gesture — somebody scanning the list for
   * "the wall by the window" should not have to open each one to find it.
   *
   * The plan is drawn from the CAPTURE, so it knows a wall by its RoomPlan
   * element id, which the conversion stored as the surface's `externalRef`.
   * The database id the list keys on means nothing to it.
   */
  const active = surfaces.find((s) => s.id === (hovered ?? picked)) ?? null;
  const highlightId = active?.externalRef ?? null;
  const highlightLabel = active === null ? undefined : (active.name ?? active.kind);

  return (
    <>
      <div className="page-header">
        <p className="small">
          <a href={href.surveyRooms(page.survey.id)}>All rooms</a>
        </p>
        <h1>{room.name}</h1>
        <p className="lede">
          {room.floorAreaM2 === null ? "No area yet" : `${room.floorAreaM2.value.toFixed(1)} m²`}
          {room.designTempC === null ? "" : ` · warm to ${room.designTempC}°`}
          {" · "}
          {stillToAnswer(owed, assumedSides)}
        </p>
      </div>

      {/* THE PICTURE THAT SAYS WHICH WALL. "Wall 1" is what the scan called
          it; standing in the room it means nothing. Sticky, so it stays in
          view while the list is scrolled and answered. */}
      {floorRooms !== null && room.externalRef != null ? (
        <div className="room-plan-strip">
          <RoomMiniPlan
            rooms={floorRooms}
            activeRef={room.externalRef}
            highlightId={highlightId}
            {...(highlightLabel === undefined ? {} : { highlightLabel })}
          />
          <p className="room-plan-strip__note">
            {highlightLabel === undefined
              ? `${room.name} is the filled room. Point at a wall below to find it.`
              : `${highlightLabel} is the heavy line.`}
          </p>
        </div>
      ) : null}

      <RoomIdentity room={room} canEdit={canEdit} onSaved={reload} />

      <ul className="room-surfaces" data-testid="room-surfaces">
        {surfaces.map((surface) => {
          const owing = outstanding(surface, countsInternal);
          const on = surface.id === picked;
          return (
            <li key={surface.id}>
              <button
                type="button"
                className={`room-surface${owing ? " room-surface--owing" : ""}${on ? " room-surface--on" : ""}`}
                aria-expanded={on}
                onClick={() => setPicked(on ? null : surface.id)}
                onMouseEnter={() => setHovered(surface.id)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(surface.id)}
                onBlur={() => setHovered(null)}
              >
                <span className="room-surface__who">
                  <span className="room-surface__name">{surface.name ?? surface.kind}</span>
                  <span className="room-surface__about">
                    {adjacencyLabel(surface.adjacency, surface.kind)}
                    {surface.areaM2 === null ? "" : ` · ${surface.areaM2.value.toFixed(1)} m²`}
                    {surface.adjacencyAssumed === true ? " · other side not confirmed" : ""}
                  </span>
                </span>
                <span className={owing ? "room-surface__owed" : "room-surface__done"}>
                  {owing ? "Needs its U-value" : "✓ Answered"}
                </span>
              </button>

              {on ? (
                <div className="room-surface__panel">
                  <SurfacePanel
                    surface={surface}
                    roomName={room.name}
                    canEdit={canEdit}
                    onSaved={reload}
                    internalNotCounted={!countsInternal}
                  />
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>

      {chosen === null && surfaces.length > 0 ? (
        <p className="room-surfaces__hint">
          Tap a wall, floor or window to say what is on the other side of it.
        </p>
      ) : null}
    </>
  );
}
